// 178 -- protocol allowlist before any href / navigation sink.
// Stored URLs (sponsor website, school website, shop redirect templates) are
// only schema-validated server-side; this is the last line before the value
// reaches an `href` attribute or `window.location`. Anything that is not an
// absolute http(s) URL (or a same-origin relative path) is rejected so script
// schemes like `javascript:` / `data:` can never navigate.

const HTTP_RE = /^https?:\/\//i;

/**
 * Returns `url` only if it is a safe-to-navigate http(s) URL or a relative
 * path (`/...`). Returns `undefined` otherwise -- callers must treat
 * `undefined` as "do not render the link / do not navigate".
 */
export function safeHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const trimmed = url.trim();
  if (HTTP_RE.test(trimmed)) return trimmed;
  // Allow same-origin relative paths (but not protocol-relative `//host`).
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  return undefined;
}

/**
 * Safely parse a URL's hostname for display. Returns `undefined` for malformed
 * or non-http(s) values so callers can fall back to the raw string instead of
 * throwing (a thrown `new URL()` in render white-screens the page).
 */
export function safeHostname(url: string | null | undefined): string | undefined {
  const href = safeHref(url);
  if (!href || !HTTP_RE.test(href)) return undefined;
  try {
    return new URL(href).hostname;
  } catch {
    return undefined;
  }
}
